import { useMemo } from "react";

import Highcharts, { TooltipFormatterContextObject } from "highcharts";
import uniq from "lodash-es/uniq";

import { HexColor } from "@mapmycustomers/shared/types/AnyColor";
import { NumericHistogramData, PinLegendRange } from "@mapmycustomers/shared/types/map/PinLegend";

import colors from "@app/styles/_colors.module.scss";

const numberOfColumnsPerBucket = 5;

const useNumericHistogramChartConfig = (config: {
  data: NumericHistogramData;
  ranges?: PinLegendRange[];
  visible?: boolean;
}): Highcharts.Options => {
  const { data, ranges, visible } = config;
  return useMemo(() => {
    if (!visible) {
      return {};
    }

    const flatData = data.flatMap((item) => item.data.map(({ count, range }) => [range[0], count]));

    const groupsStart = uniq(ranges?.map((range) => range[0] as number) ?? []);
    const seriesData: { color: HexColor; x: number; y: number }[] = [];
    const columnsCount = Math.min(groupsStart.length * numberOfColumnsPerBucket, flatData.length);
    const chunkSize = flatData.length / columnsCount;

    for (let i = 0; i < columnsCount; i++) {
      const baseIndex = Math.floor(i * chunkSize);
      let value = 0;
      for (let j = 0; j < chunkSize; j++) {
        value += flatData[baseIndex + j][1];
      }
      if (value > 0) {
        seriesData.push({
          color: colors.disabledGray,
          x: flatData[baseIndex][0],
          y: value,
        });
      }
    }

    return {
      chart: {
        height: 200,
        plotShadow: false,
        spacingBottom: 4,
        spacingLeft: 0,
        spacingRight: 16,
      },
      credits: undefined,
      legend: {
        enabled: false,
      },
      plotOptions: {
        column: {
          dataLabels: {
            enabled: false,
          },
          grouping: false,
          groupPadding: 0,
          pointInterval: 0,
          pointPadding: 0,
          stacking: "normal",
        },
      },
      series: [
        {
          colorByPoint: true,
          data: seriesData,
          name: "",
          type: "column",
        },
      ],
      title: {
        text: "",
      },
      tooltip: {
        formatter(this: TooltipFormatterContextObject): string {
          return `${this.y}`;
        },
      },
      xAxis: {
        lineColor: "black",
        lineWidth: 1,
        max: seriesData
          .map(({ x }) => x)
          .reduce((result, value) => (value > result ? value : result)),
        min: seriesData
          .map(({ x }) => x)
          .reduce((result, value) => (value < result ? value : result)),
        minorTicks: true,
        plotLines: groupsStart.map((value) => ({ value })),
        type: "linear",
      },
      yAxis: {
        gridLineWidth: 0,
        lineColor: "black",
        lineWidth: 1,
        max: seriesData
          .map(({ y }) => y)
          .reduce((result, value) => (value > result ? value : result)),
        tickWidth: 1,
        title: {
          text: "",
        },
      },
    };
  }, [data, ranges, visible]);
};

export default useNumericHistogramChartConfig;
